/**
 * Workflow Utility Commands - Helper commands for multi-step AI workflows
 *
 * These commands support workflow orchestration: sending progress messages
 * to users and generating structured output during multi-step workflows.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useSpaceStore from '../../stores/spaceStore';

const log = createFileLogger('WORKFLOW_COMMANDS', { enabled: false });

export const workflowUtilityCommands: SelfContainedCommand[] = [
  // ====== SPACE FIND WINDOW BY ENTITY ======
  {
    id: 'space_findWindowByEntity',
    description: 'Find a window that contains a specific entity by name/type and optionally focus it',
    autonomyLevel: 'auto',
    visualTreatment: 'silent',

    schema: {
      parameters: [
        {
          name: 'entityName',
          type: 'string',
          description: 'The name of the entity to find',
          required: true,
          validator: (value: string) => {
            if (!value || value.trim().length === 0) return 'Entity name cannot be empty';
            return null;
          },
          examples: ['My Document', 'Meeting Notes', 'Project Plan']
        },
        {
          name: 'entityType',
          type: 'string',
          description: 'The type of entity to find (optional, for more precise matching)',
          required: false,
          examples: ['Doc', 'Note', 'PDF']
        },
        {
          name: 'focus',
          type: 'boolean',
          description: 'Whether to focus the window if found (default: true)',
          required: false,
          defaultValue: true,
          examples: [true, false]
        }
      ]
    },

    examples: {
      good: [
        {
          input: { entityName: 'My Document', focus: true },
          expectedOutput: {
            status: 'success',
            message: '✅ Window found and focused',
            format: 'json',
            data: {
              window: {
                id: 'window-123',
                title: 'My Document',
                appType: 'docs',
                entity: { id: 'doc-456', name: 'My Document', entityType: 'Doc' }
              },
              focused: true
            }
          } as StandardToolResult,
          description: 'Find and focus a window by entity name',
          context: 'When needing to switch to a specific document'
        }
      ],
      bad: [
        {
          input: { entityName: '' },
          error: 'Entity name cannot be empty',
          why: 'Empty entity name cannot be used for searching',
          fix: 'Provide a valid entity name to search for'
        }
      ]
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { entityName, entityType, focus = true } = params;

      try {
        const { windows, bringWindowToFront } = useSpaceStore.getState();

        const matchingWindow = Object.values(windows).find(window => {
          const windowEntity = useSpaceStore.getState().getWindowEntity(window.id);
          if (!windowEntity) return false;
          const nameMatches = windowEntity.name === entityName;
          const typeMatches = !entityType || windowEntity.entityType === entityType;
          return nameMatches && typeMatches;
        });

        if (!matchingWindow) {
          return createErrorResult(
            'Window not found',
            `No window found with entity "${entityName}"${entityType ? ` of type ${entityType}` : ''}`,
            'validation',
            {
              data: {
                searchCriteria: { entityName, entityType },
                availableEntities: Object.values(windows)
                  .filter(w => w.entityId)
                  .map(w => {
                    const e = useSpaceStore.getState().getWindowEntity(w.id);
                    return e ? { name: e.name, type: e.entityType, windowId: w.id } : null;
                  })
                  .filter(Boolean)
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }

        const matchingEntity = useSpaceStore.getState().getWindowEntity(matchingWindow.id);
        log.info('[space_findWindowByEntity] Found window:', {
          windowId: matchingWindow.id,
          entityName,
          entityType: matchingEntity?.entityType,
          appType: matchingWindow.appType,
          willFocus: focus
        });

        if (focus) {
          bringWindowToFront(matchingWindow.id);
        }

        return createSuccessResult(
          `✅ Window ${focus ? 'found and focused' : 'found'}: ${matchingWindow.title || entityName}`,
          {
            window: {
              id: matchingWindow.id,
              title: matchingWindow.title,
              appType: matchingWindow.appType,
              entity: matchingEntity ? {
                id: matchingEntity._id || matchingEntity.id,
                name: matchingEntity.name,
                entityType: matchingEntity.entityType
              } : undefined
            },
            focused: focus
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'self-contained-v2',
              affectedEntities: [matchingWindow.id]
            },
            ui: {
              autoFocus: focus ? matchingWindow.id : undefined,
              refreshContext: false,
              resultPreview: 'minimal'
            }
          }
        );
      } catch (error) {
        log.error('[space_findWindowByEntity] Error:', error);
        return createErrorResult(
          'Failed to find window',
          error instanceof Error ? error.message : 'Unknown error',
          'execution',
          {
            data: { entityName, entityType },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  } as SelfContainedCommand,

  // ====== WORKFLOW SEND MESSAGE ======
  {
    id: 'workflow_sendMessage',
    description: 'Send a message to the user as part of a workflow step',
    autonomyLevel: 'auto',
    visualTreatment: 'narrative',

    schema: {
      parameters: [
        {
          name: 'text',
          type: 'string',
          description: 'The message text to send to the user',
          required: true,
          validator: (value: string) => {
            if (!value || value.trim().length === 0) return 'Message text cannot be empty';
            if (value.length > 10000) return 'Message too long (max 10k chars)';
            return null;
          },
          examples: [
            'Step 1 completed successfully!',
            'Please review the generated content before continuing.',
            'Workflow completed. Check your documents for the results.'
          ]
        },
        {
          name: 'stepName',
          type: 'string',
          description: 'Optional name of the workflow step',
          required: false,
          examples: ['Document Generation', 'Data Processing', 'Finalization']
        },
        {
          name: 'priority',
          type: 'string',
          description: 'Priority level of the message',
          required: false,
          defaultValue: 'normal',
          pattern: /^(low|normal|high|urgent)$/,
          examples: ['low', 'normal', 'high', 'urgent']
        }
      ]
    },

    examples: {
      good: [
        {
          input: {
            text: 'Step 1 completed successfully! Moving to next step.',
            stepName: 'Document Generation',
            priority: 'normal'
          },
          expectedOutput: {
            status: 'success',
            message: '✅ Workflow message sent successfully',
            format: 'json',
            data: {
              messageSent: true,
              messageLength: 52,
              stepName: 'Document Generation',
              priority: 'normal'
            }
          } as StandardToolResult,
          description: 'Send a workflow step completion message',
          context: 'When notifying user of workflow progress'
        }
      ],
      bad: [
        {
          input: { text: '' },
          error: 'Message text cannot be empty',
          why: 'Empty messages provide no value to the user',
          fix: 'Include meaningful text describing the workflow step'
        }
      ]
    },

    performance: { expectedDuration: 100, complexity: 'low' },

    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { text, stepName, priority = 'normal' } = params;

      try {
        log.info('[workflow_sendMessage] Sending workflow message:', {
          textLength: text.length,
          stepName,
          priority,
          workflowId: context.workflowId,
          isWorkflow: context.isWorkflow
        });

        const messagePreview = text.length > 100 ? text.substring(0, 100) + '...' : text;

        return createSuccessResult(
          `✅ Workflow message sent successfully${stepName ? ` (${stepName})` : ''}`,
          {
            messageSent: true,
            messageLength: text.length,
            stepName,
            priority,
            messagePreview,
            workflowId: context.workflowId,
            isWorkflow: context.isWorkflow
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'self-contained-v2',
              affectedEntities: [context.workflowId || context.threadId]
            },
            ui: {
              refreshContext: false,
              resultPreview: 'summary'
            }
          }
        );
      } catch (error) {
        log.error('[workflow_sendMessage] Error:', error);
        return createErrorResult(
          'Failed to send workflow message',
          error instanceof Error ? error.message : 'Unknown error',
          'execution',
          {
            data: {
              textLength: text?.length || 0,
              stepName,
              priority,
              workflowId: context.workflowId
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  } as SelfContainedCommand,

  // ====== WORKFLOW GENERATE SCHOOL TABLE ======
  {
    id: 'workflow_generateSchoolTable',
    description: 'Generate a formatted table of medical schools for the Secondary Prompt Roster',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',

    schema: {
      parameters: [
        {
          name: 'schools',
          type: 'array',
          description: 'Array of school names from user input',
          required: true,
          validator: (value: any[]) => {
            if (!Array.isArray(value)) return 'Schools must be an array';
            if (value.length === 0) return 'At least one school must be provided';
            if (value.length > 50) return 'Too many schools (max 50)';
            if (value.some(school => typeof school !== 'string' || school.trim().length === 0)) {
              return 'All school names must be non-empty strings';
            }
            return null;
          },
          examples: [
            ['Harvard Medical School', 'Johns Hopkins', 'Stanford'],
            ['UCSF', 'UCLA', 'UC San Diego']
          ]
        },
        {
          name: 'userInput',
          type: 'string',
          description: 'Original user input for context',
          required: false,
          examples: ['I want to apply to Harvard, Johns Hopkins, and Stanford']
        },
        {
          name: 'includeAdvice',
          type: 'boolean',
          description: 'Whether to include next steps advice in the table',
          required: false,
          defaultValue: true,
          examples: [true, false]
        }
      ]
    },

    examples: {
      good: [
        {
          input: {
            schools: ['Harvard Medical School', 'Johns Hopkins', 'Stanford'],
            includeAdvice: true
          },
          expectedOutput: {
            status: 'success',
            message: '✅ School table generated successfully',
            format: 'markdown',
            data: {
              tableContent: '## Your Medical Schools...',
              schoolCount: 3,
              schools: ['Harvard Medical School', 'Johns Hopkins', 'Stanford']
            }
          } as StandardToolResult,
          description: 'Generate a medical school table with advice',
          context: 'When organizing secondary application planning'
        }
      ],
      bad: [
        {
          input: { schools: [] },
          error: 'At least one school must be provided',
          why: 'Cannot generate a table with no schools',
          fix: 'Provide an array with at least one school name'
        }
      ]
    },

    performance: { expectedDuration: 200, complexity: 'low' },

    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      const { schools, userInput, includeAdvice = true } = params;

      try {
        log.info('[workflow_generateSchoolTable] Generating school table:', {
          schoolCount: schools.length,
          schools: schools.slice(0, 3),
          includeAdvice,
          workflowId: context.workflowId
        });

        const tableRows = schools.map((school: string) => {
          const cleanName = school.trim();
          return `| ${cleanName} | TBD | Not Started | Various | TBD | TBD |`;
        }).join('\n');

        let tableContent = `
## Your Medical Schools
*Based on your list: ${schools.join(', ')}*

| School Name | Deadline | Status | Character Limits | Key Prompts | Mission Focus |
|-------------|----------|--------|------------------|-------------|---------------|
${tableRows}`;

        if (includeAdvice) {
          tableContent += `

### Next Steps:
1. **Research deadlines** - Secondary invites typically arrive 2-4 weeks after primaries are verified
2. **Find character limits** - These vary significantly between schools (usually 1000-3000 characters)
3. **Identify key prompts** - Look for unique questions beyond standard diversity/why us prompts
4. **Study mission statements** - Essential for tailoring your responses

*Tip: Start with your top-choice schools to build momentum and perfect your approach!*`;
        }

        return createSuccessResult(
          `✅ School table generated successfully for ${schools.length} schools`,
          {
            tableContent,
            schoolCount: schools.length,
            schools: schools,
            userInput,
            includesAdvice: includeAdvice
          },
          {
            format: 'markdown',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'self-contained-v2',
              affectedEntities: [context.workflowId || context.threadId]
            },
            ui: {
              refreshContext: false,
              resultPreview: 'full'
            }
          }
        );
      } catch (error) {
        log.error('[workflow_generateSchoolTable] Error:', error);
        return createErrorResult(
          'Failed to generate school table',
          error instanceof Error ? error.message : 'Unknown error',
          'execution',
          {
            data: {
              schoolCount: schools?.length || 0,
              userInput,
              workflowId: context.workflowId
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  } as SelfContainedCommand,
];
